import React, { useState, useRef, useEffect } from 'react';
import { ScrollView, View, KeyboardAvoidingView, Platform, Pressable } from 'react-native';
import { YStack, XStack, H4, H5, SizableText, Button, Input, TextArea } from 'tamagui';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import getDaysAgo from '@utils/getDaysAgo';
import CommentsFlatList from '@navigation/screens/MidLevel/WelcomeScreen/Widgets/Components/CommentsFlatList';
import * as Animatable from 'react-native-animatable';
import ErrorBoundary from '@utils/ErrorBoundary';

interface Thread {
  _id: string;
  title: string;
  content: string;
  topicId: string;
  topic?: {
    _id: string;
    title: string;
    color?: string;
  };
  replies: number;
  views: number;
  likes: number;
  isSticky?: boolean;
  isLocked?: boolean;
  isSolved?: boolean;
  tags?: string[];
  createdBy: {
    _id: string;
    name: string;
    surname: string;
    avatar?: string;
    role?: string;
  };
  createdAt: string;
  updatedAt?: string;
  status?: 'open' | 'closed' | 'solved';
}

interface Comment {
  _id: string;
  content: string;
  threadId: string;
  parentId?: string;
  depth: number;
  likes: number;
  replies: Comment[];
  isEdited?: boolean;
  createdBy: {
    _id: string;
    name: string;
    surname: string;
    avatar?: string;
    role?: string;
  };
  createdAt: string;
  updatedAt?: string;
}

interface ThreadDetailProps {
  thread: Thread | null;
  comments: Comment[];
  loading: boolean;
  onBack: () => void;
  onLike: (threadId: string) => void;
  onSolve?: (threadId: string) => void;
  onLock?: (threadId: string) => void;
  onReply: (content: string, parentId?: string) => void;
  currentUserId?: string;
  canModerate?: boolean;
}

const ThreadDetail: React.FC<ThreadDetailProps> = ({
  thread,
  comments,
  loading,
  onBack,
  onLike,
  onSolve,
  onLock,
  onReply,
  currentUserId,
  canModerate = false
}) => {
  const [replyContent, setReplyContent] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [replyToId, setReplyToId] = useState<string | undefined>();
  const theme = themeSelector();
  const scrollViewRef = useRef<ScrollView>(null);

  const colors = {
    background: theme ? '#ffffff' : '#1f2937',
    cardBackground: theme ? '#f8fafc' : '#374151',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    accent: thread?.topic?.color || '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
  };

  useEffect(() => {
    if (isReplying) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [isReplying]);

  const handleReply = () => {
    if (replyContent.trim()) {
      onReply(replyContent, replyToId);
      setReplyContent('');
      setIsReplying(false);
      setReplyToId(undefined);
    }
  };

  const handleCommentReply = (parentId: string) => {
    setReplyToId(parentId);
    setIsReplying(true);
  };

  if (loading || !thread) {
    return (
      <YStack flex={1} padding="$4" gap="$4" alignItems="center" justifyContent="center">
        <MaterialCommunityIcons 
          name="loading" 
          size={32} 
          color={colors.textSecondary} 
        />
        <SizableText color={colors.textSecondary}>Loading discussion...</SizableText>
      </YStack>
    );
  }

  const getStatusIcon = () => {
    if (thread.isSolved) return 'check-circle';
    if (thread.isLocked) return 'lock';
    if (thread.status === 'closed') return 'archive';
    return 'message-text';
  };

  const getStatusColor = () => {
    if (thread.isSolved) return colors.success;
    if (thread.isLocked) return colors.danger;
    if (thread.status === 'closed') return colors.warning;
    return colors.accent;
  };

  return (
    <KeyboardAvoidingView 
      style={{ flex: 1, backgroundColor: colors.background }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView
        ref={scrollViewRef}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        {/* Header */}
        <XStack 
          padding="$4" 
          paddingBottom="$2" 
          alignItems="center" 
          gap="$3"
          borderBottomWidth={1}
          borderBottomColor={colors.border}
        >
          <Pressable onPress={onBack}>
            <MaterialIcons name="arrow-back" size={24} color={colors.text} />
          </Pressable>
          <YStack flex={1}>
            <SizableText color={colors.textSecondary} size="$2">
              {thread.topic?.title || 'Discussion'}
            </SizableText>
            <H5 color={colors.text} numberOfLines={1}>Thread Details</H5>
          </YStack>
        </XStack>

        {/* Thread Content */}
        <Animatable.View animation="fadeInUp" duration={400}>
          <YStack padding="$4" gap="$3">
            {/* Title & Status */}
            <XStack alignItems="flex-start" gap="$3">
              <View style={{
                width: 40,
                height: 40,
                backgroundColor: `${getStatusColor()}15`,
                borderRadius: 20,
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <MaterialCommunityIcons 
                  name={getStatusIcon() as any} 
                  size={20} 
                  color={getStatusColor()} 
                />
              </View>

              <YStack flex={1} gap="$2">
                <XStack alignItems="center" gap="$2" flexWrap="wrap">
                  {thread.isSticky && (
                    <MaterialIcons name="push-pin" size={16} color={colors.warning} />
                  )}
                  <H4 color={colors.text} flex={1}>
                    {thread.title}
                  </H4>
                  {thread.isLocked && (
                    <MaterialIcons name="lock" size={16} color={colors.danger} />
                  )}
                </XStack>

                {/* Tags */}
                {thread.tags && thread.tags.length > 0 && (
                  <XStack gap="$1" flexWrap="wrap">
                    {thread.tags.map((tag, index) => (
                      <View
                        key={index}
                        style={{
                          backgroundColor: `${colors.accent}20`,
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                          borderRadius: 6,
                        }}
                      >
                        <SizableText color={colors.accent} size="$2">
                          {tag}
                        </SizableText>
                      </View>
                    ))}
                  </XStack>
                )}

                {/* Author & Date */}
                <XStack alignItems="center" gap="$2">
                  <MaterialCommunityIcons 
                    name="account-circle" 
                    size={16} 
                    color={colors.textSecondary} 
                  />
                  {
                    thread.createdBy && (
                      <ErrorBoundary>
                        <SizableText color={colors.textSecondary} size="$2">
                          by {thread.createdBy.name} {thread.createdBy.surname}
                        </SizableText>
                        {thread.createdBy.role && (
                          <View
                            style={{
                              backgroundColor: colors.cardBackground,
                              paddingHorizontal: 6,
                              paddingVertical: 1,
                              borderRadius: 4,
                            }}
                          >
                            <SizableText color={colors.textSecondary} size="$1">
                              {thread.createdBy.role}
                            </SizableText>
                          </View>
                        )}
                      </ErrorBoundary>
                    )
                  }
                  <SizableText color={colors.textSecondary} size="$2">
                    • {getDaysAgo(thread.createdAt)}
                  </SizableText>
                </XStack>
              </YStack>
            </XStack>

            {/* Content */}
            <YStack 
              padding="$4" 
              backgroundColor={colors.cardBackground}
              borderRadius="$3"
              gap="$3"
            >
              <SizableText color={colors.text} size="$3" lineHeight={20}>
                {thread.content}
              </SizableText>
            </YStack>

            {/* Actions & Stats */}
            <XStack justifyContent="space-between" alignItems="center" marginTop="$2">
              <XStack gap="$4" alignItems="center">
                <Pressable onPress={() => onLike(thread._id)}>
                  <XStack alignItems="center" gap="$1">
                    <MaterialCommunityIcons 
                      name="heart" 
                      size={18} 
                      color={colors.accent} 
                    />
                    <SizableText color={colors.textSecondary} size="$2">
                      {thread.likes}
                    </SizableText>
                  </XStack>
                </Pressable>

                <XStack alignItems="center" gap="$1">
                  <MaterialCommunityIcons 
                    name="comment-multiple" 
                    size={18} 
                    color={colors.textSecondary} 
                  />
                  <SizableText color={colors.textSecondary} size="$2">
                    {thread.replies}
                  </SizableText>
                </XStack>

                <XStack alignItems="center" gap="$1">
                  <MaterialCommunityIcons 
                    name="eye" 
                    size={18} 
                    color={colors.textSecondary} 
                  />
                  <SizableText color={colors.textSecondary} size="$2">
                    {thread.views}
                  </SizableText>
                </XStack>
              </XStack>

              {/* Moderation Actions */}
              {canModerate && (
                <XStack gap="$2" alignItems="center">
                  {onSolve && !thread.isSolved && !thread.isLocked && (
                    <Pressable onPress={() => onSolve(thread._id)}>
                      <MaterialCommunityIcons 
                        name="check-circle-outline" 
                        size={20} 
                        color={colors.success} 
                      />
                    </Pressable>
                  )}
                  {onLock && (
                    <Pressable onPress={() => onLock(thread._id)}>
                      <MaterialCommunityIcons 
                        name={thread.isLocked ? "lock-open" : "lock"} 
                        size={20} 
                        color={colors.danger} 
                      />
                    </Pressable>
                  )}
                </XStack>
              )}
            </XStack>
          </YStack>
        </Animatable.View>

        {/* Comments Section */}
        <YStack flex={1} marginTop="$2">
          <XStack padding="$4" paddingBottom="$2" alignItems="center" gap="$2">
            <MaterialCommunityIcons 
              name="comment-text-multiple" 
              size={20} 
              color={colors.textSecondary} 
            />
            <H5 color={colors.text}>
              Replies ({comments.length})
            </H5>
          </XStack>

          {comments.length > 0 ? (
            <CommentsFlatList 
              comments={comments}
              feedId={thread._id}
              removeCallback={undefined}
              emptyPlaceHolder="No replies yet. Be the first to respond!"
            />
          ) : (
            <YStack 
              padding="$6" 
              alignItems="center" 
              justifyContent="center"
              gap="$3"
            >
              <MaterialCommunityIcons 
                name="comment-outline" 
                size={48} 
                color={colors.textSecondary} 
              />
              <SizableText color={colors.textSecondary} textAlign="center">
                No replies yet. Be the first to respond!
              </SizableText>
            </YStack>
          )}
        </YStack>

        {/* Reply Section */}
        {!thread.isLocked && (
          <YStack 
            padding="$4" 
            borderTopWidth={1}
            borderTopColor={colors.border}
            backgroundColor={colors.background}
          >
            {!isReplying ? (
              <Button
                onPress={() => setIsReplying(true)}
                backgroundColor={colors.accent}
                color="white"
                icon={<MaterialCommunityIcons name="reply" size={18} color="white" />}
              >
                Reply to Thread
              </Button>
            ) : (
              <Animatable.View animation="slideInUp" duration={300}>
                <YStack gap="$3">
                  {replyToId && (
                    <XStack alignItems="center" gap="$2" opacity={0.7}>
                      <MaterialCommunityIcons 
                        name="reply" 
                        size={16} 
                        color={colors.textSecondary} 
                      />
                      <SizableText color={colors.textSecondary} size="$2">
                        Replying to comment
                      </SizableText>
                      <Pressable onPress={() => setReplyToId(undefined)}>
                        <MaterialIcons name="close" size={16} color={colors.textSecondary} />
                      </Pressable>
                    </XStack>
                  )}

                  <TextArea
                    placeholder="Share your thoughts..."
                    value={replyContent}
                    onChangeText={setReplyContent}
                    backgroundColor={colors.cardBackground}
                    borderColor={colors.border}
                    color={colors.text}
                    minHeight={80}
                    textAlignVertical="top"
                  />

                  <XStack gap="$2" justifyContent="flex-end">
                    <Button
                      onPress={() => {
                        setIsReplying(false);
                        setReplyContent('');
                        setReplyToId(undefined);
                      }}
                      variant="outlined"
                      borderColor={colors.border}
                      color={colors.textSecondary}
                    >
                      Cancel
                    </Button>
                    <Button
                      onPress={handleReply}
                      backgroundColor={colors.accent}
                      color="white"
                      disabled={!replyContent.trim()}
                      opacity={replyContent.trim() ? 1 : 0.5}
                    >
                      Post Reply
                    </Button>
                  </XStack>
                </YStack>
              </Animatable.View>
            )}
          </YStack>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

export default ThreadDetail;
